import { Fragment, useEffect, useState } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils/classnames";
import { Checkbox } from "@/components/ui/check-box";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useProfile } from "@/hooks/profile/use-profile";
import { formatPhoneUS } from "@/utils/functions";
import {
  addresseeEmail,
  addresseeNamePriority,
  addresseePhone,
} from "@/utils/invoice";
import { convertThousandSeparator } from "@/scripts/functions";
import {
  ArrowDownUp,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  XCircle,
} from "lucide-react";
import { useSendReminder } from "@/hooks/invoice/use-send-reminder";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";

import { toast } from "react-toastify";
import { InvoicePDFDocument } from "@/pages/invoices/components/invoice-pdf";
import { useSettings } from "@/hooks/setting/use-settings";
import { Button } from "@/components/ui/button-tw";
import {
  getInvoiceById,
  sendInvoiceToCustomer,
  uploadInvoicePDF,
} from "@/api/v1-jwt/invoice/invoice";
import { useQueryClient } from "@tanstack/react-query";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import {
  DRAFT_INVOICE_STATUS,
  PAID_INVOICE_STATUS,
  PAID_OFF_PLATFORM_STATUS,
  PAST_DUE_INVOICE_STATUS,
  PENDING_INVOICE_STATUS,
  PROCESSING_INVOICE_STATUS,
  UNPAID_INVOICE_STATUS,
} from "@/constants/invoice";
import { useDeleteInvoice } from "@/hooks/invoice/use-delete-invoice";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useUpdateInvoice } from "@/hooks/invoice/use-update-invoice";
import { useInvoiceDrawer } from "@/hooks/invoice/use-invoice-drawer";
import { useAuth } from "@/providers/auth";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useAccountBalance } from "@/hooks/account/use-account-balance";
import { useGetPaymentMethods } from "@/hooks/payment/use-get-payment-methods";
import { BsBank } from "react-icons/bs";
import { Plaid } from "@/components/payment-methods";
import { InitiateLinkAccount } from "@/components/payment-methods/plaid-link-card";
import { usePayInvoice } from "@/hooks/transaction/use-pay-invoice";
import dayjs from "dayjs";
import { useCheckDepositDrawer } from "@/hooks/invoice/use-check-deposit-drawer";
import { useRefundDrawer } from "@/hooks/invoice/use-refund-drawer";
import { CancelPaymentModal } from "@/components/invoices/cancel-payment-modal";
import { UpdatePaymentMethod } from "@/components/payment-methods/PaymentCard";
import { hasPermission } from "@/utils/auth";
import { capitalizeFirstLetter } from "@/utils/string-manipulation";
import api from "@/api/v1-jwt/config/api";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const formatLink = (link) => {
  try {
    new URL(link);
    return link;
  } catch (e) {
    return `https://${link}`;
  }
};

function Loading() {
  return (
    <div>
      <div role="status" className="px-6 py-8 animate-pulse">
        <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[330px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[300px] mb-2.5"></div>
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px]"></div>
        <span className="sr-only">Loading...</span>
      </div>
      <div
        role="status"
        className=" p-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24 mb-2.5"></div>
            <div className="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 w-12"></div>
        </div>
        <span className="sr-only">Loading...</span>
      </div>
      <div
        role="status"
        className="absolute bottom-0 inset-x-0 p-4 space-y-4 border border-gray-200 divide-y divide-gray-200 rounded shadow animate-pulse dark:divide-gray-700 md:p-6 dark:border-gray-700"
      ></div>
    </div>
  );
}

function Error() {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            There was an error loading the invoice details.
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <ul role="list" className="list-disc space-y-1 pl-5">
              <li>Make sure your internet connection is stable</li>
              <li>Try closing and opening the drawer again</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceSlideIn() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { handleOpen: handleDepositOpen } = useCheckDepositDrawer();
  const { handleOpen: handleRefundOpen } = useRefundDrawer();
  const {
    drawerData: invoice,
    open,
    isPayable,
    isPaying,
    togglePay,
    handleClose,
    isLoading,
    isError,
  } = useInvoiceDrawer();
  const { data: paymentMethods, isLoading: loadingPaymentMethods } =
    useGetPaymentMethods({ staleTime: 0 });
  const { data: balance } = useAccountBalance();
  const { data: tenancy } = useTenancy();
  const { data: settings } = useSettings();
  const { data: profile } = useProfile({
    enabled: open,
  });

  const [cancelPaymentOpen, setCancelPaymentOpen] = useState(false);
  const [changeToPaid, setChangeToPaid] = useState(false);
  const [changeToPaidOffPlatform, setChangeToPaidOffPlatform] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openPlaid, setOpenPlaid] = useState(false);
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateData, setInitiateData] = useState(null);
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [paymentError, setPaymentError] = useState(null);

  const { mutate: payInvoice, isLoading: paying } = usePayInvoice();
  const { mutate: updateInvoice, isLoading: updating } = useUpdateInvoice();
  const { mutate: deleteInvoice, isLoading: deleting } = useDeleteInvoice();
  const { mutate: sendEmailReminder, isLoading: sendingEmail } =
    useSendReminder();

  const { user, cognitoId, clientId, userRole } = useAuth();

  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const handleSendEmailReminder = async () => {
    if (!invoice)
      return toast.warn("Please select an invoice to send a reminder.");

    sendEmailReminder(invoice?.invoice_id, {
      onSuccess: () => toast.success("Your invoice email has been sent."),
      onError: (error) => {
        console.log("ERROR::EMAIL::", error);
        toast.error(
          "Something went wrong. Please try to send the email again.",
        );
      },
    });
  };

  const handleEditInvoice = () => {
    if (!hasPermission(user, "invoice", "update", invoice)) {
      return toast.error("This invoice is not editable.");
    }

    history.push(`/invoices/edit/${invoice.invoice_id}`);
  };

  const getInvoiceinfoById = async (id, invoice_number) => {
    const res = await getInvoiceById({ userId, invoiceId: id });

    const pdfBlob = await pdf(
      <InvoicePDFDocument
        settingData={settings}
        profile={profile}
        invoice={res}
        tenancy={tenancy}
      />,
    ).toBlob();
    if (pdfBlob) {
      const folderName = `invoices/${id}`;
      const params = `folder_name=${folderName}&file_name=invoice_${invoice_number}.pdf`;
      await uploadInvoicePDF({ userId, params, pdfBlob });
    }
  };

  const handleSendInvoice = async (
    invoiceId,
    invoiceNumber,
    deliveryMethod,
  ) => {
    const payload = {
      invoice_status: "unpaid",
      invoice_delivery_method: deliveryMethod,
    };

    try {
      updateInvoice(
        { id: invoiceId, ...payload },
        {
          onSuccess: async () => {
            await getInvoiceinfoById(invoiceId, invoiceNumber);
            await sendInvoiceToCustomer({ userId, invoiceId });
            queryClient.invalidateQueries({ queryKey: ["invoices"] });
            toast.success(`Your invoice has been sent.`);
          },
          onError: () =>
            toast.error(`Something went wrong while updating the invoice.`),
        },
      );
    } catch (error) {
      toast.error(`Something went wrong while sending the invoice.`);
    }
  };

  const handlePay = async ({
    paymentMethodId = null,
    paymentType = "ach",
    disclosure,
  }) => {
    if (!isPayable) {
      toast.error("This invoice is not payable.");
    }

    const payload = {
      invoice_from: invoice?.invoice_info?.invoice_from,
      payment_type: paymentType, //selectedPayment === "instant_transfer" ? "book" : "ach",
      user_id: invoice?.invoice_info?.invoice_from?.user_id,
      client_id: userId,
      invoice_id: invoice?.invoice_id,
      payment_method_id: paymentMethodId,
      auth_approved: true,
      auth_content: disclosure,
    };

    payInvoice(payload, {
      onSuccess: () => {
        setPaymentError(null);
        toast.success("Your invoice has been paid.");
        queryClient.invalidateQueries(["invoices"]);
        queryClient.invalidateQueries(["platform"]);
        togglePay();
      },
      onError: (err) => {
        const message =
          err.status === 409
            ? "This invoice has been paid or is being processed."
            : "Payment couldn't be processed. Please try again in a moment.";
        console.error("[PAY_INVOICE] Error paying the invoice:", err);
        setPaymentError(message);
      },
    });
  };

  const bookDisclosureLanguage = `By clicking “Confirm” you authorize a one-time debit of ${Intl.NumberFormat(
    "en-US",
    {
      currency: "USD",
      currencyDisplay: "symbol",
      currencySign: "standard",
      style: "currency",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  ).format(
    invoice?.invoice_info?.invoice_total || 0,
  )} from your designated account to ${
    invoice?.invoice_info?.invoice_from?.name
  } on ${dayjs().format(
    "LL",
  )}. Your authorization is effective immediately and the transfer will be processed today or on the next business day if we receive your request after our ACH processing deadline.`;

  useEffect(() => {
    if (userId && invoice?.invoice_id) {
      api
        .get(`/platform/invoices/${userId}/${invoice?.invoice_id}/preview`)
        .then((data) => {
          setInvoiceTemplate(atob(data.data.base64_html));
        })
        .catch((error) => console.log(error));
    }
  }, [invoice?.invoice_id, userId]);

  async function downloadInvoicePdf() {
    if (!invoiceTemplate) return;

    const hiddenDiv = document.createElement("div");
    hiddenDiv.style.position = "absolute";
    hiddenDiv.style.left = "-9999px";
    hiddenDiv.style.top = "0";
    hiddenDiv.style.width = "794px";
    hiddenDiv.innerHTML = invoiceTemplate;
    document.body.appendChild(hiddenDiv);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(hiddenDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: hiddenDiv.offsetWidth,
        height: hiddenDiv.scrollHeight,
        windowWidth: 794,
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfWidth = 210;
      const pdfHeight = 297;
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const mmPerPx = pdfWidth / canvasWidth;
      const pageHeightPx = pdfHeight / mmPerPx;

      let yOffset = 0;
      let pageIndex = 0;
      while (yOffset < canvasHeight) {
        const sliceHeight = Math.min(pageHeightPx, canvasHeight - yOffset);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = sliceHeight;
        pageCanvas.getContext("2d").drawImage(
          canvas, 0, yOffset, canvasWidth, sliceHeight, 0, 0, canvasWidth, sliceHeight
        );
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(pageCanvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, pdfWidth, sliceHeight * mmPerPx);
        yOffset += sliceHeight;
        pageIndex++;
      }

      const invoiceNumber = invoice?.invoice_info?.invoice_number;
      pdf.save(`Invoice_${invoiceNumber ?? new Date().toISOString().split("T")[0]}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      document.body.removeChild(hiddenDiv);
    }
  }

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClose}>
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="relative pointer-events-auto w-screen max-w-2xl">
                    <div className=" pb-[120px] flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      <div className="px-4 py-6 sm:px-6 bg-skin-primary">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-white">
                            Invoice
                            {!isLoading &&
                            !isError &&
                            invoice?.invoice_info?.invoice_number
                              ? ` #${invoice?.invoice_info?.invoice_number}`
                              : ""}
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md text-gray-50 hover:text-gray-300 focus:ring-2 focus:ring-white"
                              onClick={handleClose}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Main */}
                      {isLoading ? (
                        <Loading />
                      ) : isError ? (
                        <Error />
                      ) : (
                        <div className=" divide-y divide-gray-200">
                          <div className="pb-6">
                            <div className="flex-col space-y-4 px-4 sm:px-6 min-h-full">
                              <div className="flex justify-between items-center mt-6 sm:flex-1">
                                <div>
                                  <div className="flex items-center">
                                    <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                                      $
                                      {convertThousandSeparator(
                                        Number(
                                          invoice?.invoice_info.invoice_total,
                                        ),
                                      )}
                                    </h3>
                                    <InvoiceStatusBadge
                                      status={
                                        invoice?.invoice_info
                                          .invoice_frequency === "scheduled"
                                          ? "Scheduled"
                                          : invoice?.invoice_info
                                              .invoice_status ||
                                            DRAFT_INVOICE_STATUS
                                      }
                                    />
                                    {invoice?.invoice_info
                                      .invoice_payment_method_type &&
                                      invoice?.invoice_info
                                        .invoice_payment_method_type !==
                                        "none" && (
                                        <span
                                          className={cn(
                                            "inline-flex items-center rounded-md bg-purple-50 ml-2 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20",
                                          )}
                                        >
                                          <span className="">
                                            {invoice?.invoice_info.invoice_payment_method_type?.toLowerCase() ===
                                            "ach"
                                              ? "ACH"
                                              : invoice?.invoice_info.invoice_payment_method_type
                                                  .replace(
                                                    /^[-_]*(.)/,
                                                    (_, c) => c.toUpperCase(),
                                                  )
                                                  .replace(
                                                    /[-_]+(.)/g,
                                                    (_, c) =>
                                                      " " + c.toUpperCase(),
                                                  )}
                                          </span>
                                        </span>
                                      )}
                                  </div>
                                </div>
                                <div className="flex gap-x-3">
                                  <div className="flex flex-wrap space-y-3 sm:space-x-3 sm:space-y-0">
                                    <div className="ml-3 inline-flex sm:ml-0">
                                      {[
                                        UNPAID_INVOICE_STATUS,
                                        PAST_DUE_INVOICE_STATUS,
                                      ].includes(
                                        invoice?.invoice_info?.invoice_status,
                                      ) &&
                                        isPayable && (
                                          // <PayInvoiceModal invoice={invoice}>
                                          <Button
                                            variant="outline"
                                            className="ml-2"
                                            onClick={() => { setPaymentError(null); togglePay(); }}
                                          >
                                            {isPaying
                                              ? "Invoice Details"
                                              : "Pay Invoice"}
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                          </Button>
                                          // </PayInvoiceModal>
                                        )}
                                      {!isPayable && (
                                        <Menu
                                          as="div"
                                          className="relative inline-block text-left"
                                        >
                                          <Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                            Options
                                            <ChevronDown
                                              className="-mr-1 h-5 w-5 text-gray-400"
                                              aria-hidden="true"
                                            />
                                          </Menu.Button>
                                          <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-100"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                          >
                                            <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                              <div className="py-1">
                                                {invoice?.invoice_info?.invoice_status
                                                  .toLowerCase()
                                                  .startsWith("unpaid") && (
                                                  <Menu.Item>
                                                    {({ active }) => (
                                                      <div
                                                        onClick={
                                                          handleSendEmailReminder
                                                        }
                                                        className={cn(
                                                          active
                                                            ? "bg-gray-100 text-gray-900"
                                                            : "text-gray-700",
                                                          "block px-4 py-2 text-sm cursor-pointer disabled:cursor-not-allowed",
                                                        )}
                                                      >
                                                        Send reminder
                                                      </div>
                                                    )}
                                                  </Menu.Item>
                                                )}
                                                {hasPermission(
                                                  user,
                                                  "mrdc",
                                                  "create",
                                                  invoice,
                                                ) && (
                                                  <Menu.Item>
                                                    {({ active }) => (
                                                      <div
                                                        onClick={() =>
                                                          handleDepositOpen(
                                                            invoice?.invoice_id,
                                                          )
                                                        }
                                                        className={cn(
                                                          active
                                                            ? "bg-gray-100 text-gray-900"
                                                            : "text-gray-700",
                                                          "block px-4 py-2 text-sm cursor-pointer disabled:cursor-not-allowed",
                                                        )}
                                                      >
                                                        Deposit check for
                                                        invoice
                                                      </div>
                                                    )}
                                                  </Menu.Item>
                                                )}
                                                {hasPermission(
                                                  user,
                                                  "invoice",
                                                  "update",
                                                  invoice,
                                                ) && (
                                                  <Menu.Item>
                                                    {({ active }) => (
                                                      <div
                                                        onClick={
                                                          handleEditInvoice
                                                        }
                                                        className={cn(
                                                          active
                                                            ? "bg-gray-100 text-gray-900"
                                                            : "text-gray-700",
                                                          "block px-4 py-2 text-sm cursor-pointer disabled:cursor-not-allowed",
                                                        )}
                                                      >
                                                        Edit
                                                      </div>
                                                    )}
                                                  </Menu.Item>
                                                )}
                                                {[
                                                  PENDING_INVOICE_STATUS,
                                                  PROCESSING_INVOICE_STATUS,
                                                ].includes(
                                                  invoice?.invoice_info
                                                    ?.invoice_status,
                                                ) &&
                                                  invoice?.invoice_info?.invoice_payment_method_type?.toLowerCase() ===
                                                    "credit_card" && (
                                                  <Menu.Item>
                                                    {({ active }) => (
                                                      <div
                                                        onClick={() => setCancelPaymentOpen(true)}
                                                        className={cn(
                                                          active
                                                            ? "bg-red-50 text-red-700"
                                                            : "text-red-600",
                                                          "block px-4 py-2 text-sm cursor-pointer",
                                                        )}
                                                      >
                                                        Cancel Payment
                                                      </div>
                                                    )}
                                                  </Menu.Item>
                                                )}
                                                {[
                                                  PAID_INVOICE_STATUS,
                                                  PAID_OFF_PLATFORM_STATUS,
                                                ].includes(
                                                  invoice?.invoice_info
                                                    ?.invoice_status,
                                                ) &&
                                                  invoice?.invoice_info?.invoice_payment_method_type?.toLowerCase() ===
                                                    "credit_card" && (
                                                  <Menu.Item>
                                                    {({ active }) => (
                                                      <div
                                                        onClick={() =>
                                                          handleRefundOpen(
                                                            invoice?.invoice_id,
                                                          )
                                                        }
                                                        className={cn(
                                                          active
                                                            ? "bg-gray-100 text-gray-900"
                                                            : "text-gray-700",
                                                          "block px-4 py-2 text-sm cursor-pointer disabled:cursor-not-allowed",
                                                        )}
                                                      >
                                                        Issue Refund
                                                      </div>
                                                    )}
                                                  </Menu.Item>
                                                )}
                                                {invoice?.invoice_info
                                                  ?.invoice_status !==
                                                  "draft" && (
                                                  <Menu.Item>
                                                    {({ active }) => (
                                                      <div
                                                        onClick={
                                                          downloadInvoicePdf
                                                        }
                                                        className={cn(
                                                          active
                                                            ? "bg-gray-100 text-gray-900"
                                                            : "text-gray-700",
                                                          "block px-4 py-2 text-sm cursor-pointer disabled:cursor-not-allowed",
                                                        )}
                                                      >
                                                        Download
                                                      </div>
                                                    )}
                                                  </Menu.Item>
                                                )}
                                                {hasPermission(
                                                  user,
                                                  "invoice",
                                                  "delete",
                                                  invoice,
                                                ) && (
                                                  <Menu.Item>
                                                    {({ active }) => (
                                                      <div
                                                        onClick={() =>
                                                          setConfirmDelete(true)
                                                        }
                                                        className={cn(
                                                          active
                                                            ? "bg-gray-100 text-gray-900"
                                                            : "text-gray-700",
                                                          "block px-4 py-2 text-sm cursor-pointer disabled:cursor-not-allowed",
                                                        )}
                                                      >
                                                        Delete
                                                      </div>
                                                    )}
                                                  </Menu.Item>
                                                )}
                                              </div>
                                            </Menu.Items>
                                          </Transition>
                                        </Menu>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="px-4 py-5 sm:px-6 sm:py-0 sm:pb-8">
                            {isPaying ? (
                              <div className="pt-6 space-y-8">
                                {/* Payment error */}
                                {paymentError && (
                                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                                    <XCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-red-800">Payment Failed</p>
                                      <p className="text-sm text-red-700 mt-0.5">{paymentError}</p>
                                    </div>
                                    <button
                                      className="text-red-400 hover:text-red-600 shrink-0"
                                      onClick={() => setPaymentError(null)}
                                      aria-label="Dismiss"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                                {/* Transfer */}
                                <div className="lg:col-start-3 lg:row-end-1">
                                  <h2 className="sr-only">Instant Transfer</h2>
                                  <div className="rounded-lg bg-gray-50  ring-1 ring-gray-900/5">
                                    <div className=" border-b border-gray-900/5 px-6 py-6 flex justify-between items-center">
                                      <div className="text-sm font-semibold leading-6 text-gray-900 flex items-center gap-x-2">
                                        <span className="text-base">
                                          Instant Transfer
                                        </span>
                                        <HoverCard>
                                          <HoverCardTrigger>
                                            <Info className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                                          </HoverCardTrigger>
                                          <HoverCardContent>
                                            Money can be transferred from your
                                            current balance. This is the fastest
                                            option.
                                          </HoverCardContent>
                                        </HoverCard>
                                      </div>

                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button
                                            type="button"
                                            disabled={
                                              balance?.available_balance <
                                                invoice.invoice_info
                                                  .invoice_total || paying
                                            }
                                          >
                                            Transfer
                                            <ArrowDownUp className="w-4 h-4 ml-2" />
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>
                                              Confirm your payment
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                              <span
                                                className={cn(
                                                  "text-sm text-muted-foreground text-center sm:text-left",
                                                )}
                                              >
                                                {bookDisclosureLanguage || ""}
                                              </span>
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel
                                              disabled={paying}
                                            >
                                              Cancel
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => {
                                                handlePay({
                                                  paymentType: "book",
                                                  paymentMethodId: null,
                                                  disclosure:
                                                    bookDisclosureLanguage,
                                                });
                                              }}
                                              disabled={paying}
                                            >
                                              Confirm
                                              {paying && (
                                                <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                                              )}
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </div>
                                    <dl className="flex flex-wrap">
                                      <div className="flex-auto pl-6 pt-6">
                                        <div className="flex items-center gap-x-2">
                                          <dt className="text-sm font-semibold leading-6 text-gray-500">
                                            Total balance
                                          </dt>
                                          {balance?.available_balance <
                                            invoice.invoice_info
                                              .invoice_total && (
                                            <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                                              Insufficient funds
                                            </span>
                                          )}
                                        </div>
                                        <dd className="mt-1 text-2xl font-semibold leading-6 text-gray-900">
                                          $
                                          {convertThousandSeparator(
                                            balance?.available_balance,
                                          )}
                                          <span className="text-gray-500 text-lg ml-1">
                                            USD
                                          </span>
                                        </dd>
                                      </div>
                                    </dl>
                                    <div className="mt-6 border-t border-gray-900/5 px-6 "></div>
                                  </div>
                                </div>
                                <div className="relative">
                                  <div
                                    aria-hidden="true"
                                    className="absolute inset-0 flex items-center"
                                  >
                                    <div className="w-full border-t " />
                                  </div>
                                  <div className="relative flex justify-center">
                                    <span className="bg-white px-2 text-sm text-gray-500">
                                      Or
                                    </span>
                                  </div>
                                </div>
                                <div className="mx-auto ">
                                  <h2 className="text-base font-semibold leading-6 text-gray-900">
                                    Payment methods
                                  </h2>
                                  <p className="mt-1 text-sm text-gray-500">
                                    Pay the invoice using one of the connected
                                    payment methods.
                                  </p>
                                  <ul
                                    role="list"
                                    className="mt-6 divide-y divide-gray-200 border-b border-t border-gray-200"
                                  >
                                    {paymentMethods?.map((paymentMethod) => (
                                      <PaymentMethodItem
                                        key={paymentMethod.payment_method_id}
                                        pm={paymentMethod}
                                        invoice={invoice}
                                        onPay={handlePay}
                                        paying={paying}
                                      />
                                    ))}
                                  </ul>
                                </div>
                                {hasPermission(
                                  user,
                                  "payment_method",
                                  "create",
                                ) && (
                                  <div className="relative overflow-hidden rounded-lg bg-white border px-4 pb-4 pt-5 text-left  transition-all sm:my-8 w-full  sm:p-6">
                                    <div>
                                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-slate-200/80">
                                        <BsBank
                                          aria-hidden="true"
                                          className="h-6 w-6 text-slate-800"
                                        />
                                      </div>
                                      <div className="mt-3 text-center sm:mt-5">
                                        <div
                                          as="h3"
                                          className="text-base font-semibold leading-6 text-gray-900"
                                        >
                                          Connect Payment Method
                                        </div>
                                        <div className="mt-2">
                                          <p className="text-sm text-gray-500">
                                            Add a new payment method to pay this
                                            invoice.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="mt-5 sm:mt-6">
                                      <Button
                                        variant="secondary"
                                        type="button"
                                        className="w-full"
                                        onClick={() => setInitiateOpen(true)}
                                        disabled={openPlaid}
                                      >
                                        Connect
                                        {openPlaid && (
                                          <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                                        )}
                                      </Button>
                                    </div>
                                    <InitiateLinkAccount
                                      open={initiateOpen}
                                      onOpenChange={setInitiateOpen}
                                      onSubmit={(values) => {
                                        setInitiateData(values);
                                        setOpenPlaid(true);
                                      }}
                                    />
                                    {openPlaid && initiateData ? (
                                      <Plaid
                                        open={openPlaid}
                                        onClose={() => setOpenPlaid(false)}
                                        method_type={"PLAID"}
                                        payment_method_status={""}
                                        access_token=""
                                        linkData={initiateData}
                                      />
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                {/* Invoice */}
                                <dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
                                  <div className="col-span-2 text-left mb-6 sm:mt-0">
                                    {invoice?.invoice_info
                                      ?.invoice_external_id ? (
                                      <>
                                        <dt className="inline text-gray-500">
                                          Reference Number:{" "}
                                        </dt>
                                        {invoice?.invoice_info
                                          ?.invoice_external_id_link ? (
                                          <dd className="inline text-gray-700 hover:text-gray-900 border-b border-gray-700 hover:cursor-pointer">
                                            <a
                                              className=""
                                              href={formatLink(
                                                invoice?.invoice_info
                                                  ?.invoice_external_id_link,
                                              )}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {
                                                invoice?.invoice_info
                                                  ?.invoice_external_id
                                              }
                                            </a>
                                          </dd>
                                        ) : (
                                          <dd className="inline text-gray-700">
                                            {
                                              invoice?.invoice_info
                                                ?.invoice_external_id
                                            }
                                          </dd>
                                        )}
                                      </>
                                    ) : (
                                      <dt className="inline text-gray-500">
                                        No Reference Number
                                      </dt>
                                    )}
                                  </div>
                                  <div className="border-t border-gray-900/5 pt-6 sm:pr-4 flex gap-x-1">
                                    <dt className="inline text-gray-500">
                                      {invoice?.invoice_info
                                        ?.invoice_send_date ? (
                                        <p>Issued on</p>
                                      ) : (
                                        <p className="flex items-center gap-x-2">
                                          <span>No issued date</span>

                                          <HoverCard>
                                            <HoverCardTrigger>
                                              <Info className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                                            </HoverCardTrigger>
                                            <HoverCardContent>
                                              The issue date is the day the
                                              invoice is sent, or the day it is
                                              scheduled to be sent.
                                            </HoverCardContent>
                                          </HoverCard>
                                        </p>
                                      )}
                                    </dt>{" "}
                                    <dd className="inline text-gray-700">
                                      <time>
                                        {invoice?.invoice_info
                                          ?.invoice_send_date &&
                                          moment(
                                            invoice?.invoice_info
                                              ?.invoice_send_date,
                                          ).format("LL")}
                                      </time>
                                    </dd>
                                  </div>
                                  <div className="border-t border-gray-900/5 pt-6 mt-2 sm:mt-0 sm:pl-4">
                                    {invoice?.invoice_info
                                      ?.invoice_due_date && (
                                      <>
                                        <dt className="inline text-gray-500">
                                          {invoice?.invoice_info
                                            ?.invoice_payment_terms !==
                                            "none" &&
                                          invoice?.invoice_info?.invoice_payment_terms.startsWith(
                                            "net",
                                          )
                                            ? `${invoice?.invoice_info?.invoice_payment_terms
                                                .split("-")
                                                .map(
                                                  (word) =>
                                                    word
                                                      .charAt(0)
                                                      .toUpperCase() +
                                                    word.slice(1).toLowerCase(),
                                                )
                                                .join(" ")} due on`
                                            : "Due on"}
                                        </dt>{" "}
                                        <dd className="inline text-gray-700">
                                          <time>
                                            {moment(
                                              invoice?.invoice_info
                                                ?.invoice_due_date,
                                            ).format("LL")}
                                          </time>
                                        </dd>
                                      </>
                                    )}
                                    {!invoice?.invoice_info?.invoice_due_date &&
                                      invoice?.invoice_info
                                        ?.invoice_payment_terms !== "none" && (
                                        <>
                                          <dt className="inline text-gray-500">
                                            Terms:
                                          </dt>
                                          <dd className="inline text-gray-700">
                                            {` ${invoice?.invoice_info?.invoice_payment_terms
                                              .split("-")
                                              .map(
                                                (word) =>
                                                  word.charAt(0).toUpperCase() +
                                                  word.slice(1).toLowerCase(),
                                              )
                                              .join(" ")}`}
                                          </dd>
                                        </>
                                      )}
                                  </div>

                                  <div className="mt-6 border-t border-gray-900/5 pt-6 sm:pr-4">
                                    <dt className="font-semibold text-gray-900">
                                      From
                                    </dt>
                                    <dd className="mt-2 text-gray-500">
                                      <span className="font-medium text-gray-900">
                                        {isPayable
                                          ? invoice?.invoice_info?.invoice_from
                                              ?.name
                                          : profile?.business_name}
                                      </span>
                                      <br />
                                      {isPayable
                                        ? invoice?.invoice_info?.invoice_from
                                            ?.email
                                        : profile?.business_email}
                                      <br />
                                      {formatPhoneUS(
                                        isPayable
                                          ? invoice?.invoice_info?.invoice_from
                                              ?.phone
                                          : profile?.business_phone,
                                      )}
                                    </dd>
                                  </div>

                                  <div className="mt-8 sm:mt-6 sm:border-t sm:border-gray-900/5 sm:pl-4 sm:pt-6">
                                    <dt className="font-semibold text-gray-900">
                                      To
                                    </dt>
                                    <dd className="mt-2 text-gray-500">
                                      <span className="font-medium text-gray-900">
                                        {addresseeNamePriority(invoice)}
                                      </span>

                                      <br />
                                      {addresseeEmail(invoice) || ""}
                                      <br />
                                      {formatPhoneUS(addresseePhone(invoice))}
                                      <br />
                                    </dd>
                                  </div>
                                </dl>

                                <table className="mt-16 w-full whitespace-nowrap text-left text-sm leading-6">
                                  <colgroup>
                                    <col className="w-full" />
                                    <col />
                                    <col />
                                    <col />
                                  </colgroup>
                                  <thead className="border-b border-gray-200 text-gray-900">
                                    <tr>
                                      <th
                                        scope="col"
                                        className="px-0 py-3 font-semibold"
                                      >
                                        Line Items
                                      </th>
                                      <th
                                        scope="col"
                                        className="hidden py-3 pl-8 pr-0 text-right font-semibold sm:table-cell"
                                      >
                                        Units
                                      </th>
                                      <th
                                        scope="col"
                                        className="hidden py-3 pl-8 pr-0 text-right font-semibold sm:table-cell"
                                      >
                                        Rate
                                      </th>
                                      <th
                                        scope="col"
                                        className="py-3 pl-8 pr-0 text-right font-semibold"
                                      >
                                        Price
                                      </th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {(
                                      invoice?.invoice_info
                                        ?.invoice_line_items || []
                                    ).map((item, i) => (
                                      <tr
                                        key={i}
                                        className="border-b border-gray-100"
                                      >
                                        <td className="max-w-0 px-0 py-5 align-top">
                                          <div className="truncate font-medium text-gray-900">
                                            {item.description}
                                          </div>
                                        </td>
                                        <td className="hidden py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700 sm:table-cell">
                                          {item.quantity}
                                        </td>
                                        <td className="hidden py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700 sm:table-cell">
                                          $
                                          {convertThousandSeparator(
                                            Number(item.unit_price),
                                          )}
                                        </td>
                                        <td className="py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700">
                                          $
                                          {convertThousandSeparator(
                                            Number(item.item_total),
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr>
                                      <th
                                        scope="row"
                                        className="px-0 pb-0 pt-6 font-normal text-gray-700 sm:hidden"
                                      >
                                        Subtotal
                                      </th>
                                      <th
                                        scope="row"
                                        colSpan={3}
                                        className="hidden px-0 pb-0 pt-6 text-right font-normal text-gray-700 sm:table-cell"
                                      >
                                        Subtotal
                                      </th>
                                      <td className="pb-0 pl-8 pr-0 pt-6 text-right tabular-nums text-gray-900">
                                        $
                                        {convertThousandSeparator(
                                          Number(
                                            invoice?.invoice_info
                                              .invoice_subtotal,
                                          ),
                                        )}
                                      </td>
                                    </tr>

                                    <tr>
                                      <th
                                        scope="row"
                                        className="pt-4 font-normal text-gray-700 sm:hidden"
                                      >
                                        Shipping Fee
                                      </th>
                                      <th
                                        scope="row"
                                        colSpan={3}
                                        className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
                                      >
                                        Shipping Fee
                                      </th>
                                      <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
                                        $
                                        {convertThousandSeparator(
                                          Number(
                                            invoice?.invoice_info
                                              .invoice_shipping_fee,
                                          ),
                                        )}
                                      </td>
                                    </tr>

                                    <tr>
                                      <th
                                        scope="row"
                                        className="pt-4 font-normal text-gray-700 sm:hidden"
                                      >
                                        Tax
                                      </th>
                                      <th
                                        scope="row"
                                        colSpan={3}
                                        className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
                                      >
                                        Tax
                                      </th>
                                      <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
                                        $
                                        {convertThousandSeparator(
                                          Number(
                                            invoice?.invoice_info
                                              .invoice_tax_amount,
                                          ),
                                        )}
                                      </td>
                                    </tr>

                                    {!!invoice?.invoice_info
                                      ?.provider_surcharge_amount &&
                                      invoice?.invoice_info
                                        ?.invoice_payment_method_type
                                        ?.toLowerCase() ===
                                        "credit_card" && (
                                      <tr>
                                        <th
                                          scope="row"
                                          className="pt-4 font-normal text-gray-700 sm:hidden"
                                        >
                                          Surcharge Fee
                                        </th>
                                        <th
                                          scope="row"
                                          colSpan={3}
                                          className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
                                        >
                                          Surcharge Fee
                                        </th>
                                        <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
                                          $
                                          {convertThousandSeparator(
                                            Number(
                                              invoice?.invoice_info
                                                .provider_surcharge_amount,
                                            ),
                                          )}
                                        </td>
                                      </tr>
                                    )}
                                    {invoice?.invoice_info
                                      .invoice_pass_processing_fee && (
                                      <tr>
                                        <th
                                          scope="row"
                                          className="pt-4 font-normal text-gray-700 sm:hidden"
                                        >
                                          Processing Fee
                                        </th>
                                        <th
                                          scope="row"
                                          colSpan={3}
                                          className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
                                        >
                                          Processing Fee
                                        </th>
                                        <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
                                          $
                                          {convertThousandSeparator(
                                            Number(
                                              invoice?.invoice_info
                                                .invoice_processing_fee,
                                            ),
                                          )}
                                        </td>
                                      </tr>
                                    )}
                                    <tr>
                                      <th
                                        scope="row"
                                        className="pt-4 font-semibold text-gray-900 sm:hidden"
                                      >
                                        Total
                                      </th>
                                      <th
                                        scope="row"
                                        colSpan={3}
                                        className="hidden pt-4 text-right font-semibold text-gray-900 sm:table-cell"
                                      >
                                        Total
                                      </th>
                                      <td className="pb-0 pl-8 pr-0 pt-4 text-right font-semibold tabular-nums text-gray-900">
                                        $
                                        {convertThousandSeparator(
                                          Number(
                                            invoice?.invoice_info.invoice_total,
                                          ),
                                        )}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>

                                <div className="mt-8 sm:mt-6 pt-4 mb-8 sm:border-t sm:border-gray-900/5 text-sm">
                                  <h4 className="font-semibold text-gray-900">
                                    Notes
                                  </h4>
                                  <div className="mt-2 text-gray-500">
                                    {invoice?.invoice_info?.invoice_notes}
                                  </div>
                                </div>
                                {[
                                  UNPAID_INVOICE_STATUS,
                                  PAST_DUE_INVOICE_STATUS,
                                ].includes(
                                  invoice?.invoice_info?.invoice_status,
                                ) &&
                                  !isPayable && (
                                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
                                      <div className="flex gap-x-3">
                                        <div className="flex items-center gap-x-2">
                                          <Checkbox
                                            id="paid"
                                            checked={changeToPaid}
                                            onCheckedChange={() => {
                                              setChangeToPaid((curr) => !curr);
                                            }}
                                          />
                                          <label
                                            htmlFor="paid"
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                          >
                                            Mark this invoice as paid
                                          </label>
                                        </div>

                                        <button
                                          type="button"
                                          className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 cursor-pointer shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:bg-white disabled:cursor-not-allowed"
                                          onClick={() => {
                                            if (!invoice) return;
                                            updateInvoice(
                                              {
                                                id: invoice?.invoice_id,
                                                invoice_status:
                                                  PAID_INVOICE_STATUS,
                                              },
                                              {
                                                onSuccess: () => {
                                                  toast.success(
                                                    "Invoice marked as paid.",
                                                  );

                                                  setChangeToPaid(false);
                                                },
                                                onError: () => {
                                                  toast.error(
                                                    "Ops some problem occurred while updating invoice. Please try again.",
                                                  );
                                                },
                                              },
                                            );
                                          }}
                                          disabled={
                                            !changeToPaid ||
                                            !invoice ||
                                            deleting ||
                                            updating
                                          }
                                        >
                                          Save
                                        </button>
                                      </div>

                                      <div className="flex gap-x-3">
                                        <div className="flex items-center gap-x-2">
                                          <Checkbox
                                            id="paid"
                                            checked={changeToPaidOffPlatform}
                                            onCheckedChange={() => {
                                              setChangeToPaidOffPlatform(
                                                (curr) => !curr,
                                              );
                                            }}
                                          />
                                          <label
                                            htmlFor="paid"
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                          >
                                            Mark this invoice as paid (Off
                                            Platform).
                                          </label>
                                        </div>

                                        <button
                                          type="button"
                                          className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 cursor-pointer shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:bg-white disabled:cursor-not-allowed"
                                          onClick={() => {
                                            if (!invoice) return;
                                            updateInvoice(
                                              {
                                                id: invoice?.invoice_id,
                                                invoice_status:
                                                  PAID_OFF_PLATFORM_STATUS,
                                              },
                                              {
                                                onSuccess: () => {
                                                  toast.success(
                                                    "Invoice marked as paid off platform.",
                                                  );

                                                  setChangeToPaidOffPlatform(
                                                    false,
                                                  );
                                                },
                                                onError: () => {
                                                  toast.error(
                                                    "Ops some problem occurred while updating invoice. Please try again.",
                                                  );
                                                },
                                              },
                                            );
                                          }}
                                          disabled={
                                            !changeToPaidOffPlatform ||
                                            !invoice ||
                                            deleting ||
                                            updating
                                          }
                                        >
                                          Save
                                        </button>
                                      </div>
                                      {invoice.invoice_info
                                        .invoice_frequency !== "scheduled" && (
                                        <button
                                          type="button"
                                          disabled={sendingEmail}
                                          onClick={handleSendEmailReminder}
                                          className="w-full inline-flex mt-6 flex-shrink-0 items-center justify-center rounded-md bg-skin-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-skin-primary/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-skin-primary sm:flex-1"
                                        >
                                          Send email reminder to customer
                                        </button>
                                      )}
                                    </div>
                                  )}

                                {hasPermission(
                                  user,
                                  "invoice",
                                  "update",
                                  invoice,
                                ) &&
                                  [DRAFT_INVOICE_STATUS].includes(
                                    invoice?.invoice_info?.invoice_status,
                                  ) && (
                                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
                                      <button
                                        type="button"
                                        disabled={sendingEmail}
                                        onClick={handleEditInvoice}
                                        className="w-full inline-flex mt-6 flex-shrink-0 items-center justify-center rounded-md bg-skin-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-skin-primary/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-skin-primary sm:flex-1"
                                      >
                                        Edit & Send
                                      </button>
                                    </div>
                                    // <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
                                    //   <DropdownMenu className=" ">
                                    //     <DropdownMenuTrigger
                                    //       asChild
                                    //       className="w-full"
                                    //     >
                                    //       <Button className="w-full bg-skin-primary">
                                    //         Send Invoice{" "}
                                    //         <ChevronDown className="w-4 h-4 ml-2" />
                                    //       </Button>
                                    //     </DropdownMenuTrigger>
                                    //     <DropdownMenuContent className="w-[300px] sm:w-[640px]">
                                    //       <DropdownMenuLabel>
                                    //         Send invoice to contact
                                    //       </DropdownMenuLabel>

                                    //       <DropdownMenuSeparator />
                                    //       <DropdownMenuItem
                                    //         onClick={() =>
                                    //           handleSendInvoice(
                                    //             invoice?.invoice_id,
                                    //             invoice?.invoice_info?.invoice_number,
                                    //             "email"
                                    //           )
                                    //         }
                                    //         className="focus:bg-gray-100 px-4 py-2 -mx-2 rounded-none"
                                    //       >
                                    //         Send by email
                                    //       </DropdownMenuItem>
                                    //       <DropdownMenuItem
                                    //         onClick={() =>
                                    //           handleSendInvoice(
                                    //             invoice?.invoice_id,
                                    //             invoice?.invoice_info?.invoice_number,
                                    //             "text"
                                    //           )
                                    //         }
                                    //         className="focus:bg-gray-100 px-4 py-2 -mx-2 rounded-none"
                                    //       >
                                    //         Send by SMS
                                    //       </DropdownMenuItem>
                                    //       <DropdownMenuItem
                                    //         onClick={() =>
                                    //           handleSendInvoice(
                                    //             invoice?.invoice_id,
                                    //             invoice?.invoice_info?.invoice_number,
                                    //             "all"
                                    //           )
                                    //         }
                                    //         className="focus:bg-gray-100 px-4 py-2 -mx-2 rounded-none"
                                    //       >
                                    //         Send by email and SMS
                                    //       </DropdownMenuItem>
                                    //     </DropdownMenuContent>
                                    //   </DropdownMenu>
                                    // </div>
                                  )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              invoice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteInvoice(invoice?.invoice_id, {
                  onSuccess: () => {
                    setConfirmDelete(false);
                    handleClose();
                    toast.success("Your invoice was successfully deleted.");
                  },
                  onError: () => {
                    setConfirmDelete(false);
                    toast.error(
                      "There was a problem deleting this invoice. Please try again.",
                    );
                  },
                });
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CancelPaymentModal
        open={cancelPaymentOpen}
        onOpenChange={setCancelPaymentOpen}
        invoice={invoice}
        onSuccess={() => {
          setCancelPaymentOpen(false);
          handleClose();
          queryClient.invalidateQueries(["invoices"]);
        }}
      />
    </>
  );
}

function PaymentMethodItem({ pm, invoice, onPay, paying }) {
  const history = useHistory();
  const [plaidVerifyOpen, setPlaidVerifyOpen] = useState(false);

  const disclosureLanguage = `By clicking “Confirm” you authorize a one-time debit of ${Intl.NumberFormat(
    "en-US",
    {
      currency: "USD",
      currencyDisplay: "symbol",
      currencySign: "standard",
      style: "currency",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  ).format(
    invoice?.invoice_info?.invoice_total || 0,
  )} from your designated account to ${
    invoice?.invoice_info?.invoice_from?.name
  } on ${dayjs().format(
    "LL",
  )}. Your authorization is effective immediately and the transfer will be processed today or on the next business day if we receive your request after our ACH processing deadline.`;

  return (
    <Fragment key={pm.payment_method_id}>
      <li>
        <div className="group relative flex items-start space-x-3 py-4">
          <div className="flex-shrink-0">
            <span
              className={cn(
                "bg-slate-500 inline-flex h-10 w-10 items-center justify-center rounded-lg",
                {
                  "bg-yellow-500":
                    pm.payment_method_status ===
                      "pending_manual_verification" ||
                    pm.payment_method_status === "pending_disconnect",
                },
              )}
            >
              <BsBank aria-hidden="true" className="h-6 w-6 text-white" />
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900">
              {`${pm.payment_method_nickname || pm.institution_info?.institution_name || pm.payment_method_data.institution?.name || pm.payment_method_data.item?.institution_name || "Unknown Bank"} ${pm.payment_method_nickname ? "" : capitalizeFirstLetter(pm.payment_method_data.account?.subtype || pm.payment_method_data.ach?.account_type || "")}`}
            </div>
            <p className="text-sm text-gray-500">
              {`***${pm.payment_method_data.account?.mask || pm.payment_method_data.ach?.account_number || ""}`}
              {pm.payment_method_status === "pending_disconnect" && (
                <span className="text-red-600 ml-3">
                  Expiring soon.{" "}
                  <span
                    className="border-b border-b-red-600 hover:text-red-700 hover:cursor-pointer"
                    onClick={() => history.push("/payment-methods")}
                  >
                    Click here to verify
                  </span>
                </span>
              )}
              {pm.payment_method_status === "expired" && (
                <span className="text-red-600 ml-3">Connection expired</span>
              )}
            </p>
          </div>
          <div className="flex-shrink-0 self-center">
            {pm.payment_method_status === "pending_manual_verification" ? (
              <Button
                className="bg-yellow-500 hover:bg-yellow-500/90"
                onClick={() => setPlaidVerifyOpen(true)}
                disabled={plaidVerifyOpen}
              >
                Verify
                {plaidVerifyOpen ? (
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                ) : (
                  <ChevronRight className="w-4 h-4 ml-2" />
                )}
              </Button>
            ) : pm.payment_method_status === "expired" ? (
              <UpdatePaymentMethod
                paymentMethodId={pm.payment_method_id}
                name={pm.payment_method_data.name_on_account}
                paymentMethodDefault={
                  pm.payment_method_data.payment_method_default
                }
                className="w-full"
              />
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" disabled={paying}>
                    Pay
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm your payment</AlertDialogTitle>
                    <AlertDialogDescription>
                      <span
                        className={cn(
                          "text-sm text-muted-foreground text-center sm:text-left",
                        )}
                      >
                        {disclosureLanguage || ""}
                      </span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={paying}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      disabled={paying}
                      onClick={() => {
                        onPay({
                          paymentType: "ach",
                          paymentMethodId: pm.payment_method_id,
                          disclosure: disclosureLanguage,
                        });
                      }}
                    >
                      Confirm
                      {paying && (
                        <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </li>
      {plaidVerifyOpen && (
        <Plaid
          open={plaidVerifyOpen}
          onClose={() => setPlaidVerifyOpen(false)}
          method_type={pm.payment_method_type}
          payment_method_status={pm.payment_method_status}
          access_token={pm.payment_method_data?.access_token || ""}
          key={pm.payment_method_id}
          name_on_account={pm.payment_method_data?.name_on_account}
          linkData={{
            firstName: pm?.account_first_name,
            lastName: pm?.account_last_name,
            businessName: pm?.account_business_name,
            accountType: pm?.account_type,
          }}
        />
      )}
    </Fragment>
  );
}
